import * as React from "react";
import {LoadingViewState, RouterView} from "../RouterView";
import {Form, FormListener} from "../../form/Form";
import {Validators} from "../../form/Validators";
import {FormGroup} from "../../form/FormGroup";
import {FormInput} from "../../form/FormInput";
import {FormTextArea} from "../../form/FormTextArea";
import {FormDatePicker} from "../../form/FormDatePicker";
import {SelectInput} from "../../form/SelectInput";
import {Submit} from "../../form/Submit";
import {RequiresAdminRole} from "../../util/RequiresAdminRole";
import {AwardItem, ItemView} from "../../../../generated/protocol";
import {ITEM_SERVICE_NAME, ItemService} from "../../../service/admin/ItemService";
import {kernel} from "../../../ioc/IOCHelper";
import {ItemAwardView} from "./ItemAwardView";
import {FormSubmitError} from "../../form/FormFeedback";
import {DeleteButtonModal} from "../../util/DeleteButtonModal";
import {Response} from "axios";
import {DASHBOARD_USER_SERVICE_NAME, DashboardUserService} from "../../../service/DashboardUserService";
import {FeatureToggleService} from "../../../service/FeatureToggleService";


interface State extends LoadingViewState {
    item?: ItemView;
    validationMap?: any;
    award?: AwardItem;
}

export class ItemDetailView extends RouterView<{ params: { id: string } }, State> implements FormListener<ItemView> {

    itemService: ItemService;
    dashboardUserService: DashboardUserService;

    useOperatorId: false;

    private coinTypeChanged: boolean;
    private categoryChanged: boolean;
    private itemsWithCoinTypes: string[];

    constructor() {
        super();
        this.state = {loading: true, validationMap: {}};
        this.itemService = kernel.get<ItemService>(ITEM_SERVICE_NAME);
        this.dashboardUserService = kernel.get<DashboardUserService>(DASHBOARD_USER_SERVICE_NAME);
        this.itemsWithCoinTypes = ['TICKET', 'JSNG_ENTRY'];
    }

    loadContent(): Promise<any> {
        return this.init();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.params.id != this.props.params.id) {
            this.init();
        }
    }

    init() {
        if (this.props.params.id == "new") {
            return undefined;
        } else {
            return this.loadAll(
                this.itemService.getEntity(this.props.params.id)
            ).then(results => {
                this.setState({
                    item: results[0]
                });
            })
        }
    }

    onSubmit(): Promise<any> {
        return this.itemService.save(this.state.item).then((item) => {
            if (this.props.params.id == "new") {
                this.navigate("/item/" + item.id);
            } else {
                this.setState({item: item});
            }
        }).catch((err) => {
            console.error("Server error when submitting form ", err);
            throw err.message;
        });
    }

    onSubmitError() {
        console.log(this.state.item);
    }

    deleteItem() {
        return this.itemService.remove(this.state.item.id).then(() => {
            this.navigate("/item");
        }).catch((err: Response) => {
            throw "Unable to delete leaderboard " + err.statusText;
        });

    }

    formDidUpdate(formModel: ItemView, validationMap: any) {
        if(this.categoryChanged) {
            formModel = {
                ...formModel,
                coinType: "None",
                coinAmount: ""
            }
            this.categoryChanged = false
        }
        if(this.coinTypeChanged && formModel.coinType === "None") {
            formModel = {
                ...formModel,
                coinAmount: ""
            }
            this.coinTypeChanged = false
        }
        console.log("Form model = ", formModel);
        this.setState({item: formModel, validationMap: validationMap});
    }

    renderImageAndAward() {
        let isNew: boolean = this.props.params.id == "new";
        if (isNew) {
            return "";
        }
        else {
            let canAwardItem = this.dashboardUserService.hasPermission(["ITEMS"], true);

            return (
                <div className="row">
                    <div className="col-lg-4">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content" height="300px">
                                <img src={this.state.item.imageUrl} height="200px" width="200px"/>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        <div className="ibox">
                            {canAwardItem ? <ItemAwardView params={{id: this.state.item.id, category: this.state.item.category}} /> : ""}
                        </div>
                    </div>
                </div>
            );
        }
    }

    getTopButtons() {
        if (this.props.params.id != "new") {
            return (
                <div className="page-top-actions">
                    {this.getDeleteButtons()}
                    <a onClick={() => {
                        this.navigate(`/item/${this.state.item.id}/players`)
                    }} className="btn-lg btn-primary ">Players</a>
                    <a onClick={() => {
                        this.navigate(`/item/${this.state.item.id}/history`)
                    }} className="btn-lg btn-primary ">History</a>
                </div>
            )
        } else {
            return <div/>;
        }
    }

    getDeleteButtons(): JSX.Element {
        const disableAvatarDeletion = this.state.item.category === "AVATAR" && FeatureToggleService.avatarMoveFeatureFlag.isEnabled
        const canDeleteItem = this.dashboardUserService.hasPermission(["ITEMS"], true) && !disableAvatarDeletion;
        if (!canDeleteItem) return <span/>;
        return (
            <span>
                <DeleteButtonModal className="btn-lg btn-danger" onDelete={this.deleteItem.bind(this)} description={this.state.item.name} category={this.state.item.category}/>
                {this.state.item.category === "AVATAR" && <a onClick={() => {
                    this.navigate(`/item/${this.state.item.id}/refund`)
                }} className="btn-lg btn-danger ">Revoke</a>}
            </span>
        );
    }

    getOperatorIdField() {
        if (this.useOperatorId) {
            return (
                <RequiresAdminRole>
                    <FormGroup className="form-group">
                        <label className="col-sm-2 control-label">Operator ID</label>
                        <div className="col-sm-10">
                            <FormInput type="text" model="operatorId" className="form-control"/>
                            <span className="help-block m-b-none">
                                                        The id of the operator that owns this item (can edit)
                                                    </span>
                        </div>
                    </FormGroup>
                    <div className="hr-line-dashed"></div>
                </RequiresAdminRole>
            );
        }
        else {
            return "";
        }
    }

    onCoinTypeChange = () => {
        this.coinTypeChanged = true
    }

    onCategoryChange = () => {
        this.categoryChanged = true
    }

    canSubmitForm = (): boolean => {
        const imageUrlRequired = this.state.item && this.state.item.category === "AVATAR"

        return (
            this.dashboardUserService.hasPermission(["ITEMS"], true)
            && (
                imageUrlRequired
                    ? this.state.item.imageUrl && this.state.item.imageUrl.toString().trim().length > 0
                    : true
            )
        )
    }

    renderContent() {
        let isNew: boolean = this.props.params.id == "new";
        let imageUrlRequired = this.state.item && this.state.item.category === "AVATAR";
        const imageUrlValidators = imageUrlRequired
            ? [Validators.textInput(), Validators.required()]
            : [Validators.textInput()];
        const disableAvatarUpdate = this.state.item && this.state.item.category === "AVATAR" && FeatureToggleService.avatarMoveFeatureFlag.isEnabled
        let categories = ["AVATAR", "TICKET", "JSNG_ENTRY"]
        if (FeatureToggleService.avatarMoveFeatureFlag.isEnabled && isNew) {
            categories = ["TICKET", "JSNG_ENTRY"]
        }
        if (FeatureToggleService.avatarMoveFeatureFlag.isEnabled && !isNew && this.state.item && this.state.item.category !== "AVATAR") {
            categories = ["TICKET", "JSNG_ENTRY"]
        }
        let preventCategoryChange = FeatureToggleService.avatarMoveFeatureFlag.isEnabled && !isNew && this.state.item && this.state.item.category === "AVATAR"

        const shouldDisplayCoinTypeAndAmount = this.state.item && (this.state.item.category === 'TICKET' || (FeatureToggleService.jsngEntryLiabilityFeatureFlag.isEnabled && this.itemsWithCoinTypes.indexOf(this.state.item.category) > -1));
        const shouldDisplayCoinAmount = this.state.item && this.state.item.coinType !== 'None';
        return (
            <div>
                <div className="row wrapper border-bottom white-bg page-heading">
                    <div className="col-lg-12">
                        <h1>Item : {isNew ? 'new' : this.state.item.name + "(" + this.state.item.nameId + ")"}</h1>
                    </div>
                </div>

                <div className="wrapper wrapper-content animated">

                    {this.getTopButtons()}

                    {this.renderImageAndAward()}

                    <Form className="form-horizontal" formListener={this} modelObject={this.state.item}
                          validationMap={this.state.validationMap}>

                        <div className="row">
                            <div className="col-lg-12">
                                <div className="ibox float-e-margins">
                                    <div className="ibox-title">
                                        <h5>Item Details</h5>
                                    </div>
                                    <div className="ibox-content">
                                        <div className="hr-line-dashed"></div>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Name ID</label>
                                            <div className="col-sm-10">
                                                <FormInput disabled={!isNew} type="text" model="nameId"
                                                           validators={!isNew ? [] : [Validators.required(), Validators.textId()]}
                                                           className="form-control"/>
                                                <span className="help-block m-b-none">
                                                    Unique id for the item, example: avatar-zombie
                                                </span>
                                            </div>
                                        </FormGroup>

                                        {this.getOperatorIdField()}

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Name</label>
                                            <div className="col-sm-10">
                                                <FormInput type="text" model="name" disabled={disableAvatarUpdate}
                                                           validators={[Validators.required(), Validators.textInput()]}
                                                           className="form-control"/>
                                                <span className="help-block m-b-none">
                                                    The name displayed to players
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Description</label>
                                            <div className="col-sm-10">
                                                <FormTextArea type="text" model="description"
                                                              disabled={disableAvatarUpdate}
                                                              validators={[Validators.textInput()]}
                                                              className="form-control"/>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Image URL</label>
                                            <div className="col-sm-10">
                                                <FormTextArea type="text" model="imageUrl"
                                                              disabled={disableAvatarUpdate}
                                                              validators={imageUrlValidators}
                                                              className="form-control"/>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Category</label>
                                            <div className="col-sm-10">
                                                <SelectInput className="form-control" required={true}
                                                             model="category"
                                                             values={categories}
                                                             onChange={this.onCategoryChange}
                                                             disabled={preventCategoryChange}
                                                />
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Item Value</label>
                                            <div className="col-sm-10">
                                                <FormTextArea type="text" model="value"
                                                              disabled={disableAvatarUpdate}
                                                              validators={[Validators.textInput()]}
                                                              className="form-control"/>
                                                <span className="help-block m-b-none">
                                                    Item value is a relative value indicator. More valuable items are dropped less frequently for instance.
                                                </span>
                                            </div>
                                        </FormGroup>

                                        {shouldDisplayCoinTypeAndAmount &&  <div>
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Coin Type</label>
                                                <div className="col-sm-10">
                                                    <SelectInput className="form-control" required={false}
                                                                 sort={false}
                                                                 model="coinType"
                                                                 values={["None", "Gold", "Sweeps"]}
                                                                 onChange={this.onCoinTypeChange}/>
                                                    <span className="help-block m-b-none">
                                            What is the coin type that this item will be used in
                                            </span>
                                                </div>
                                            </FormGroup>

                                            { shouldDisplayCoinAmount && <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Coin Amount</label>
                                                <div className="col-sm-10">
                                                    <FormInput type="text" model="coinAmount"
                                                               validators={[Validators.number(), Validators.min(0)]}
                                                               className="form-control"
                                                               required={true}/>
                                                    <span className="help-block m-b-none">
                                                    What is the coin amount that this item can be compared to
                                                </span>
                                                </div>
                                            </FormGroup> }
                                        </div>}

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Allows Multiples</label>
                                            <div className="col-sm-10">
                                                <FormInput model="allowMultiple" type="checkbox" disabled={disableAvatarUpdate}/>
                                                <span className="help-block m-b-none">
                                                    If checked, a single player can hold multiple of this item type.
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Expiration Date</label>
                                            <div className="col-sm-10">
                                                <FormDatePicker type="text" model="expirationDate"
                                                                disabled={disableAvatarUpdate}
                                                                className="form-control"/>
                                                <span className="help-block m-b-none">
                                                    If set, Item automatically become expired after this date
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Expiration Interval</label>
                                            <div className="col-sm-10">
                                                <FormInput type="text" model="expirationInterval"
                                                           disabled={disableAvatarUpdate}
                                                           className="form-control"/>
                                                <span className="help-block m-b-none">
                                                    If set, Item automatically become expired after the specified interval pass starting from the moment Item was awarded.
                                                    If both Interval and Date specified, the earliest will be taking in consideration.
                                                </span>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Expiration Interval Unit</label>
                                            <div className="col-sm-10">
                                                <SelectInput className="form-control" required={false}
                                                             disabled={disableAvatarUpdate}
                                                             sort={false}
                                                             model="expirationIntervalUnit"
                                                             values={["HOUR", "DAY", "WEEK", "MONTH"]}/>
                                            </div>
                                        </FormGroup>

                                        <div className="hr-line-dashed"></div>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Extras</label>
                                            <div className="col-sm-10">
                                                <div>{isNew ? "" : JSON.stringify(this.state.item.extras)}</div>
                                                <span className="help-block m-b-none">
                                                    Extra attributes, specific for given Item Category.
                                                </span>
                                            </div>
                                        </FormGroup>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {
                            disableAvatarUpdate ||
                            <div className="row">
                                <div className="col-lg-12">
                                    <FormSubmitError/>
                                    <div className="form-group">
                                        <div className="col-sm-12">
                                            <Submit
                                                disabled={!this.canSubmitForm()}
                                                className={`ladda-button btn-lg btn-primary ${this.canSubmitForm() ? `` : `disabled`}`}>
                                                Update
                                            </Submit>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </Form>
                </div>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/view/item/ItemDetailView.tsx